import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import millify from 'millify';
import { Virtuoso } from 'react-virtuoso';
import { faXmark } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { gql } from '@soundxyz/gql-string';
import { IconButton } from '@components/main-components/icon-buttons/IconButton';
import { StandardModal } from '@components/main-components/modals/StandardModal';
import { useModal } from '@context/modal';
import type { FragmentType } from '@graphql/reactQuery';
import {
  FollowersModalFragmentDoc,
  FollowersModalRelationFragmentDoc,
  getFragment,
} from '@graphql/reactQuery';
import { getSingularOrPlural } from '@utils/helpers';
import { UserRow, UserRowSkeleton } from '../shared/UserList/UserRow';
import { EmptyUserStat } from './EmptyState';
import {
  FOLLOWERS_EMPTY_HEADER,
  FOLLOWERS_EMPTY_SUBTEXT,
  MAX_UNVIRTUALIZED_ITEMS,
  StyledScrollArea,
} from './shared';
import { useFollowersFollowing } from './store';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment FollowersModal on User {
    id
    publicAddress
    followerCount
    username
  }
  fragment FollowersModalRelation on UserRelation {
    id
    userA {
      id
      ...UserModalRow
    }
  }
`);

const userItemContent = (i: number, user: FragmentType<FollowersModalRelationFragmentDoc>) => {
  const { userA } = getFragment(FollowersModalRelationFragmentDoc, user);
  return (
    <div key={`follower-${userA.id}-${i}`} className="px-6">
      <UserRow user={userA} sourceLocation="Followers Modal" />
    </div>
  );
};

export const FollowersModal = ({
  followersData,
}: {
  followersData: FragmentType<FollowersModalFragmentDoc>;
}) => {
  const { publicAddress, followerCount, username } = getFragment(
    FollowersModalFragmentDoc,
    followersData,
  );

  const {
    followerUsersData: followers,
    followersLoading: loading,
    followersLoadMore,
  } = useFollowersFollowing({
    publicAddress,
  });
  const { closeCancelModal } = useModal();

  const noFollowers = !followers || followers.length === 0;
  const [scrollParent, setScrollParent] = React.useState<HTMLDivElement | undefined>();

  const LoadingFooter = React.useCallback(
    () =>
      loading ? (
        <div className="h-6 px-6">
          <UserRowSkeleton />
          <UserRowSkeleton />
          <UserRowSkeleton />
        </div>
      ) : (
        <div className="h-6" />
      ),
    [loading],
  );

  const initalFollowes = React.useMemo(
    () => followers.slice(0, MAX_UNVIRTUALIZED_ITEMS),
    [followers],
  );

  const virtualizedFollowers = React.useMemo(
    () => followers.slice(MAX_UNVIRTUALIZED_ITEMS),
    [followers],
  );

  const body = (
    <StyledScrollArea
      ref={r => {
        setScrollParent(r || undefined);
      }}
      empty={noFollowers}
    >
      {noFollowers && !loading && (
        <EmptyUserStat
          header={FOLLOWERS_EMPTY_HEADER}
          subText={FOLLOWERS_EMPTY_SUBTEXT(username)}
        />
      )}

      {!noFollowers && (
        <>
          {/* Virtuoso takes a while to render so only shows 1 user on intial mount this it to always show prefetched data and not have to wait for virtuoso  */}
          {initalFollowes.length > 0 && initalFollowes.map((user, i) => userItemContent(i, user))}
          {virtualizedFollowers.length > 0 && (
            <Virtuoso
              data={virtualizedFollowers}
              itemContent={userItemContent}
              customScrollParent={scrollParent}
              endReached={followersLoadMore}
              components={{ Footer: LoadingFooter }}
              atTopStateChange={() => null}
            />
          )}
        </>
      )}
    </StyledScrollArea>
  );

  return (
    <StandardModal
      bodyClassName="p-0"
      containerClassName="pb-0"
      subHeader={
        <div className="flex w-full items-center justify-between pb-4">
          <h3 className="font-title text-title-l font-medium">
            {followerCount > 0
              ? `${millify(followerCount)} ${getSingularOrPlural({
                  count: followerCount,
                  word: 'follower',
                })}`
              : 'followers'}
          </h3>

          <IconButton
            icon={<FontAwesomeIcon icon={faXmark} size="lg" className="text-base500" />}
            onClick={closeCancelModal}
          />
        </div>
      }
      body={body}
      showHeaderShadow
    />
  );
};
